import ReactMarkdown from 'react-markdown'
import { SearchResult } from '../types'

interface SearchResultItemProps {
  result: SearchResult
  rank: number
}

export default function SearchResultItem({ result, rank }: SearchResultItemProps) {
  const formatRelevanceScore = (score: number) => {
    return Math.round(score * 100)
  }

  const getRelevanceBackgroundClass = (score: number) => {
    const percentage = Math.round(score * 100)
    if (percentage >= 75) {
      return 'bg-green-50 border-green-200' // Green tint for high relevance
    } else if (percentage >= 70) {
      return 'bg-yellow-50 border-yellow-200' // Yellow tint for good relevance
    } else if (percentage >= 65) {
      return 'bg-orange-50 border-orange-200' // Orange tint for moderate relevance
    } else {
      return 'bg-red-50 border-red-200' // Red tint for low relevance
    }
  }

  return (
    <article className={`${getRelevanceBackgroundClass(result.relevance)} rounded border p-4 hover:shadow-sm transition-shadow`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <span className="text-xs font-medium text-gray-400">#{rank}</span>
        </div>
        
        <div className="text-right">
          <div className="text-xs font-medium text-gray-600">
            {formatRelevanceScore(result.relevance)}%
          </div>
        </div>
      </div>

      <div className="mb-3">
        <a
          href={result.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-primary-600 hover:text-primary-700 font-medium break-all"
        >
          {result.url}
        </a>
      </div>

      <div className="bg-gray-50 border-l-4 border-gray-200 pl-3 py-2 rounded-r">
        <div className="prose prose-sm max-w-none text-gray-700 overflow-hidden">
          <span className="text-gray-500 text-xs">…</span>
          <div className="overflow-x-auto">
            <ReactMarkdown
              components={{
                // Override heading styles to be inline
                h1: ({ children }) => <span className="text-sm font-semibold text-gray-800">{children}</span>,
                h2: ({ children }) => <span className="text-sm font-semibold text-gray-800">{children}</span>,
                h3: ({ children }) => <span className="text-sm font-semibold text-gray-800">{children}</span>,
                h4: ({ children }) => <span className="text-sm font-semibold text-gray-800">{children}</span>,
                h5: ({ children }) => <span className="text-sm font-semibold text-gray-800">{children}</span>,
                h6: ({ children }) => <span className="text-sm font-semibold text-gray-800">{children}</span>,
                // Style code blocks
                code: ({ children, className }) => {
                  const isInline = !className
                  if (isInline) {
                    return <code className="px-1 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-mono">{children}</code>
                  }
                  return <code className="block p-2 bg-gray-200 text-gray-700 rounded text-xs font-mono overflow-x-auto">{children}</code>
                },
                // Style links
                a: ({ children, href }) => (
                  <a href={href} className="text-primary-600 hover:text-primary-700 underline text-xs" target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                ),
                // Style lists
                ul: ({ children }) => <ul className="list-disc list-inside space-y-0.5 text-xs">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal list-inside space-y-0.5 text-xs">{children}</ol>,
                // Style paragraphs
                p: ({ children }) => <p className="mb-1 last:mb-0 text-xs leading-relaxed break-words">{children}</p>,
              }}
            >
              {result.snippet}
            </ReactMarkdown>
          </div>
          <span className="text-gray-500 text-xs">…</span>
        </div>
      </div>
    </article>
  )
}
